import { useEffect, useRef, useState } from "react";
import type { HandoffSummary } from "@proof/handoff";
import { uid } from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Button, Control } from "./components";
import { ApiLink } from "./ApiLink";
import { HandoffDelivery } from "./HandoffDelivery";
import type { HandoffDeliveryStatus } from "@proof/core";

export function RetainHandoff({
  jobId,
  versionId,
  projectName,
  accepted,
  initials,
  comments,
  notify,
}: {
  jobId: string;
  versionId?: string;
  projectName: string;
  accepted: boolean;
  initials: string;
  comments: string;
  notify: (message: string) => void;
}) {
  const transport = useWorkspaceTransport(),
    [name, setName] = useState(projectName),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState<{ scope: string; record: HandoffSummary }>(),
    request = useRef<{ scope: string; key: string } | undefined>(undefined),
    controller = useRef<AbortController | undefined>(undefined);
  const scope = JSON.stringify([
      jobId,
      versionId,
      name,
      initials.trim(),
      comments,
      transport.principal?.tenantId,
      transport.principal?.subject,
      transport.principal?.operatorContext?.id,
    ]),
    active = useRef({ scope, transport });
  active.current = { scope, transport };
  useEffect(() => () => controller.current?.abort(), [transport]);
  const record = saved?.scope === scope ? saved.record : undefined;
  return (
    <section aria-label="Retain approved handoff" className="handoff-retain">
      <h4>Keep this approved proof</h4>
      <p>Save the reviewed PDF and customization for your ordering system.</p>
      <Control label="Handoff package name">
        <input
          maxLength={100}
          value={name}
          disabled={busy}
          onChange={(event) => setName(event.target.value)}
        />
      </Control>
      {(!accepted || !initials.trim()) && (
        <p className="muted">
          Approve the proof and enter your initials to save a package.
        </p>
      )}
      {comments.length > 4000 && (
        <p role="alert">Handoff comments are limited to 4,000 characters.</p>
      )}
      <Button
        disabled={
          busy ||
          !accepted ||
          !initials.trim() ||
          !name.trim() ||
          comments.length > 4000 ||
          !!record
        }
        onClick={() => {
          if (request.current?.scope !== scope)
            request.current = { scope, key: uid("handoff_request") };
          const abort = new AbortController();
          controller.current?.abort();
          controller.current = abort;
          setBusy(true);
          void transport.client
            .createHandoff(
              {
                jobId,
                ...(versionId ? { versionId } : {}),
                name,
                idempotencyKey: request.current.key,
                approval: {
                  accepted: true,
                  initials: initials.trim(),
                  comments,
                },
              },
              abort.signal,
            )
            .then((value) => {
              if (
                !abort.signal.aborted &&
                active.current.scope === scope &&
                active.current.transport === transport
              ) {
                setSaved({ scope, record: value });
                notify("Approved proof package saved.");
              }
            })
            .catch((error: Error) => {
              if (
                !abort.signal.aborted &&
                active.current.scope === scope &&
                active.current.transport === transport
              )
                notify(error.message);
            })
            .finally(() => {
              if (!abort.signal.aborted) setBusy(false);
            });
        }}
      >
        {busy
          ? "Saving package…"
          : record
            ? "Proof package saved"
            : "Retain approved proof"}
      </Button>
      {record && (
        <ApiLink
          className="button"
          path={`/handoffs/${record.id}/package`}
          filename={`${record.id}.zip`}
          notify={notify}
        >
          Download retained package
        </ApiLink>
      )}
    </section>
  );
}

type Page = {
  delivery: HandoffDeliveryStatus;
  records: HandoffSummary[];
  nextCursor?: string;
  storage: { durable: boolean; artifactDurable: boolean };
};
export function HandoffPackages({
  projectId,
  notify,
}: {
  projectId: string;
  notify: (message: string) => void;
}) {
  const transport = useWorkspaceTransport(),
    [collection, setCollection] = useState("document"),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [state, setState] = useState<{
      key: string;
      transport: typeof transport;
      page: Page;
    }>(),
    controller = useRef<AbortController | undefined>(undefined);
  const key = JSON.stringify([projectId, collection]),
    current = useRef({ key, transport });
  current.current = { key, transport };
  const page =
    state?.key === key && state.transport === transport
      ? state.page
      : undefined;
  const load = (cursor?: string) => {
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setLoading(true);
    setError("");
    void Promise.all([
      transport.client.listHandoffs(
        {
          ...(collection === "document" ? { projectId } : {}),
          ...(cursor ? { cursor } : {}),
        },
        abort.signal,
      ),
      transport.client.handoffDeliveryStatus(abort.signal),
    ])
      .then(([result, delivery]) => {
        if (
          abort.signal.aborted ||
          current.current.key !== key ||
          current.current.transport !== transport
        )
          return;
        setState({
          key,
          transport,
          page: {
            ...result,
            delivery,
            records:
              cursor && page
                ? [...page.records, ...result.records]
                : result.records,
          },
        });
      })
      .catch((failure: Error) => {
        if (
          !abort.signal.aborted &&
          current.current.key === key &&
          current.current.transport === transport
        )
          setError(failure.message);
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
  };
  useEffect(() => {
    load();
    return () => controller.current?.abort();
  }, [key, transport]);
  return (
    <section
      className="panel padded handoff-packages"
      aria-label="Retained proof packages"
    >
      <h2>Retained proof packages</h2>
      <p>Reviewed PDFs and customization saved under your account.</p>
      <div className="button-row">
        <Control label="Package collection">
          <select
            value={collection}
            onChange={(event) => setCollection(event.target.value)}
          >
            <option value="document">Current document</option>
            <option value="all">All accessible packages</option>
          </select>
        </Control>
        <Button disabled={loading} onClick={() => load()}>
          Refresh packages
        </Button>
      </div>
      {loading && <p role="status">Loading retained packages…</p>}
      {error && <p role="alert">{error}</p>}
      {page && (!page.storage.durable || !page.storage.artifactDurable) && (
        <p className="note-box">
          Server storage is temporary. Download a copy to keep this package.
        </p>
      )}
      {page && !page.records.length && !loading && (
        <p>No retained proof packages in this collection.</p>
      )}
      {page?.records.map((record) => (
        <div key={record.id}>
          <article className="saved-proof-row">
            <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
              <strong>{record.name}</strong>
              <small>
                {record.source.projectName} ·{" "}
                {record.source.versionId
                  ? "Published snapshot"
                  : "Demo draft snapshot"}
              </small>
              <small>
                Approved {new Date(record.createdAt).toLocaleString()} ·{" "}
                {record.approval.initials}
              </small>
              <small>
                {record.source.colorSpace.toUpperCase()} ·{" "}
                {(record.package.bytes / 1024).toFixed(0)} KB · Ready for your
                ordering system
              </small>
            </div>
            <ApiLink
              className="button"
              path={`/handoffs/${record.id}/package`}
              filename={`${record.id}.zip`}
              notify={notify}
            >
              Download package
            </ApiLink>
          </article>
          {page.delivery.available &&
            record.ownerSubjectId === transport.principal?.subject &&
            record.operatorContextId ===
              transport.principal?.operatorContext?.id && (
              <HandoffDelivery id={record.id} label={page.delivery.label} />
            )}
        </div>
      ))}
      {page?.nextCursor && (
        <Button disabled={loading} onClick={() => load(page.nextCursor)}>
          Load more packages
        </Button>
      )}
    </section>
  );
}
